import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const damageTypes = [
  {
    id: 'water',
    icon: 'ri-drop-fill',
    title: 'Water Damage',
    desc: 'Burst pipes, flooding, appliance leaks, sewage backup',
    color: 'border-teal-400 bg-teal-50',
    activeColor: 'border-teal-500 bg-teal-100',
    iconColor: 'text-teal-600',
    badge: 'Most Common',
    badgeColor: 'bg-teal-100 text-teal-700',
    img: 'https://public.readdy.ai/ai/img_res/7409571e0434b9ba64af0ae6310dca89.jpg',
  },
  {
    id: 'fire',
    icon: 'ri-fire-fill',
    title: 'Fire & Smoke Damage',
    desc: 'Fire damage, smoke stains, soot, burn marks, odor',
    color: 'border-orange-400 bg-orange-50',
    activeColor: 'border-orange-500 bg-orange-100',
    iconColor: 'text-orange-600',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=fire%20and%20smoke%20damage%20inside%20room%20with%20charred%20walls%20and%20soot%20stains%2C%20post%20fire%20damage%20interior%20scene&width=400&height=260&seq=dt002&orientation=landscape',
  },
  {
    id: 'mold',
    icon: 'ri-lungs-fill',
    title: 'Mold & Mildew',
    desc: 'Visible mold growth, musty odors, air quality concerns',
    color: 'border-green-400 bg-green-50',
    activeColor: 'border-green-500 bg-green-100',
    iconColor: 'text-green-600',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=mold%20growth%20on%20wall%20and%20ceiling%20in%20residential%20property%2C%20visible%20mold%20damage%2C%20close%20up%20realistic%20scene&width=400&height=260&seq=dt003&orientation=landscape',
  },
  {
    id: 'storm',
    icon: 'ri-thunderstorms-fill',
    title: 'Storm & Wind Damage',
    desc: 'Roof damage, broken windows, debris, structural impact',
    color: 'border-indigo-400 bg-indigo-50',
    activeColor: 'border-indigo-500 bg-indigo-100',
    iconColor: 'text-indigo-600',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=storm%20wind%20damage%20to%20roof%20and%20building%20exterior%20with%20debris%20and%20broken%20windows%2C%20severe%20weather%20damage%20scene&width=400&height=260&seq=dt004&orientation=landscape',
  },
  {
    id: 'pipe',
    icon: 'ri-tools-fill',
    title: 'Pipe Leak & Drainage',
    desc: 'Leaking pipes, clogged drains, sewage issues, hydro-jetting',
    color: 'border-cyan-400 bg-cyan-50',
    activeColor: 'border-cyan-500 bg-cyan-100',
    iconColor: 'text-cyan-600',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=pipe%20leak%20under%20sink%20with%20water%20dripping%2C%20plumbing%20damage%20and%20drainage%20issue%2C%20realistic%20close%20up%20scene&width=400&height=260&seq=dt005&orientation=landscape',
  },
  {
    id: 'biohazard',
    icon: 'ri-virus-fill',
    title: 'Biohazard Cleanup',
    desc: 'Sewage overflow, hazardous materials, decontamination',
    color: 'border-red-400 bg-red-50',
    activeColor: 'border-red-500 bg-red-100',
    iconColor: 'text-red-600',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=biohazard%20cleanup%20situation%20with%20warning%20signs%2C%20professional%20decontamination%20scene%2C%20hazardous%20material%20cleanup&width=400&height=260&seq=dt006&orientation=landscape',
  },
  {
    id: 'structural',
    icon: 'ri-building-fill',
    title: 'Structural Damage',
    desc: 'Foundation issues, wall collapse, ceiling damage, settling',
    color: 'border-amber-400 bg-amber-50',
    activeColor: 'border-amber-500 bg-amber-100',
    iconColor: 'text-amber-600',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=structural%20damage%20to%20building%20with%20cracked%20walls%20and%20damaged%20foundation%2C%20interior%20structural%20damage%20scene&width=400&height=260&seq=dt007&orientation=landscape',
  },
  {
    id: 'other',
    icon: 'ri-question-fill',
    title: 'Other / Not Sure',
    desc: 'Not sure what type of damage? We\'ll assess and advise',
    color: 'border-gray-300 bg-gray-50',
    activeColor: 'border-gray-500 bg-gray-100',
    iconColor: 'text-gray-500',
    badge: '',
    badgeColor: '',
    img: 'https://readdy.ai/api/search-image?query=property%20damage%20assessment%20inspector%20with%20clipboard%20examining%20damaged%20property%2C%20professional%20inspection%20scene&width=400&height=260&seq=dt008&orientation=landscape',
  },
];

const urgencyLevels = [
  { id: 'emergency', label: 'Emergency', sub: 'Happening right now', icon: 'ri-alarm-warning-fill', color: 'text-red-600', bg: 'bg-red-50 border-red-300' },
  { id: 'urgent', label: 'Urgent', sub: 'Within 24 hours', icon: 'ri-time-fill', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-300' },
  { id: 'soon', label: 'Soon', sub: 'Within this week', icon: 'ri-calendar-fill', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-300' },
  { id: 'scheduled', label: 'Scheduled', sub: 'Planning ahead', icon: 'ri-calendar-check-fill', color: 'text-green-600', bg: 'bg-green-50 border-green-300' },
];

const serviceRequestSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Request Restoration Service | Mitigation Restoration Services',
    description: 'Submit a service request for emergency cleanup, property restoration, or pipe leak repair. Select your damage type and urgency level to get started.',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/service-request`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { '@type': 'ListItem', position: 2, name: 'Request Service', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/service-request` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Restoration Service Request',
    description: 'Request emergency cleanup, property restoration, pipe leak repair, mold remediation, or fire damage restoration services in Houston TX.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Mitigation Restoration Services',
      telephone: '+18005551234',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
    },
    areaServed: { '@type': 'City', name: 'Houston' },
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/service-request`,
  },
];

export default function ServiceRequestPage() {
  const [selectedDamage, setSelectedDamage] = useState<string[]>([]);
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const navigate = useNavigate();

  const toggleDamage = (id: string) => {
    setSelectedDamage((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleContinue = () => {
    if (selectedDamage.length === 0 || !selectedUrgency) return;
    const params = new URLSearchParams({
      damage: selectedDamage.join(','),
      urgency: selectedUrgency,
    });
    navigate(`/customer-information?${params.toString()}`);
  };

  const canContinue = selectedDamage.length > 0 && selectedUrgency !== '';

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Request Restoration Service | Mitigation Restoration Services Houston TX"
        description="Submit a service request for emergency cleanup, property restoration, or pipe leak repair in Houston TX. Select your damage type and urgency to get started."
        keywords="request restoration service Houston, emergency service request, property damage service"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/service-request`}
        schema={serviceRequestSchema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#1E3A5F]">
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">
            <i className="ri-search-eye-fill"></i>
            Step 1 of 2
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            What Type of Damage<br />Do You Have?
          </h1>
          <p className="text-white/70 text-sm max-w-lg mx-auto">
            Select all damage types that apply to your property. This helps us dispatch the right certified specialists to your location.
          </p>
        </div>
      </section>

      {/* Damage Type Selector */}
      <section className="py-14 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 md:px-8">

          {/* Progress */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DC2626] text-white text-xs font-bold">1</div>
              <span className="text-sm font-semibold text-[#1E3A5F]">Damage Type</span>
            </div>
            <div className="w-16 h-px bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 text-xs font-bold">2</div>
              <span className="text-sm text-gray-400">Your Information</span>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mb-6">
            Select all that apply — you can choose multiple
          </p>

          {/* Damage Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {damageTypes.map((type) => {
              const isSelected = selectedDamage.includes(type.id);
              return (
                <button
                  key={type.id}
                  onClick={() => toggleDamage(type.id)}
                  className={`relative rounded-2xl border-2 overflow-hidden text-left transition-all duration-200 cursor-pointer ${
                    isSelected ? type.activeColor + ' ring-2 ring-offset-1 ring-current' : type.color + ' hover:scale-[1.02]'
                  }`}
                >
                  {/* Image */}
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={type.img}
                      alt={type.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-white">
                        <i className="ri-check-fill text-[#DC2626] text-sm font-bold"></i>
                      </div>
                    )}
                    {type.badge && (
                      <span className={`absolute top-3 left-3 text-xs font-semibold px-2 py-0.5 rounded-full ${type.badgeColor}`}>
                        {type.badge}
                      </span>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${type.icon} ${type.iconColor} text-base`}></i>
                      <h3 className="font-bold text-[#1E3A5F] text-sm">{type.title}</h3>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{type.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Urgency */}
          <div className="bg-white rounded-2xl p-6 mb-8">
            <h2 className="font-bold text-[#1E3A5F] text-base mb-4">How Urgent Is Your Situation?</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {urgencyLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setSelectedUrgency(level.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    selectedUrgency === level.id
                      ? level.bg + ' ring-2 ring-offset-1'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <i className={`${level.icon} text-2xl ${selectedUrgency === level.id ? level.color : 'text-gray-400'}`}></i>
                  <div className="text-center">
                    <p className={`font-bold text-sm ${selectedUrgency === level.id ? 'text-[#1E3A5F]' : 'text-gray-600'}`}>{level.label}</p>
                    <p className="text-xs text-gray-400">{level.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Summary & CTA */}
          {selectedDamage.length > 0 && (
            <div className="bg-[#1E3A5F] rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-white text-sm font-semibold mb-1">Selected Damage Types:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedDamage.map((id) => {
                    const type = damageTypes.find((d) => d.id === id);
                    return (
                      <span key={id} className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                        {type?.title}
                      </span>
                    );
                  })}
                </div>
              </div>
              {selectedUrgency && (
                <span className="bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap">
                  {urgencyLevels.find((u) => u.id === selectedUrgency)?.label}
                </span>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleContinue}
              disabled={!canContinue}
              className="inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white px-10 py-4 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Continue to Your Information
              <i className="ri-arrow-right-line"></i>
            </button>
            <a
              href="tel:+18005551234"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#1E3A5F] text-[#1E3A5F] px-10 py-4 rounded-full font-semibold text-sm hover:bg-[#1E3A5F] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call for Immediate Help
            </a>
          </div>
          <p className="text-center text-xs text-gray-400 mt-4">
            For life-threatening emergencies, call 911 first, then contact us at +1 (800) 555-1234
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
